import React, { useState, useEffect } from 'react';
import {
  Package,
  Search,
  Plus,
  X,
  Printer,
  Download,
  Eye
} from 'lucide-react';
import { productsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency } from '../utils/currency';
import {
  ModernCard,
  ModernButton,
  ModernInput,
  ModernSearchBar,
  ModernModal,
  ModernLoadingSpinner
} from '../components/ui/ModernComponents';
import toast from 'react-hot-toast';
import JsBarcode from 'jsbarcode';
import jsPDF from 'jspdf';
import AddProductForm from '../components/forms/AddProductForm';

const Stickers = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const { hasAnyRole } = useAuth();

  useEffect(() => {
    fetchProducts();
  }, []);

  // Handle product creation success
  const handleProductCreated = async (newProduct) => {
    try {
      // Refresh products list
      await fetchProducts();
      // Close the modal
      setShowAddProductModal(false);
      // Show success message
      toast.success('Produit ajouté avec succès');
    } catch (error) {
      console.error('Error refreshing products:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await productsAPI.getProducts();
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = products.filter(product =>
    !selectedProducts.find(p => p.id === product.id) &&
    (String(product.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
     String(product.brand || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
     String(product.model || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
     String(product.barcode || '').toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const addProductToSelection = (product) => {
    const existingProduct = selectedProducts.find(p => p.id === product.id);
    if (existingProduct) {
      setSelectedProducts(prev => 
        prev.map(p => p.id === product.id ? { ...p, quantity: (p.quantity || 1) + 1 } : p)
      );
    } else {
      setSelectedProducts(prev => [...prev, { ...product, quantity: 1 }]);
    }
  };

  const removeProductFromSelection = (productId) => {
    setSelectedProducts(prev => prev.filter(p => p.id !== productId));
  };

  const updateProductQuantity = (productId, quantity) => {
    setSelectedProducts(prev => 
      prev.map(p => p.id === productId ? { ...p, quantity: Math.max(1, quantity) } : p)
    );
  };

  const generateStickersPDF = () => {
    if (selectedProducts.length === 0) return;
    
    // A4 page size: 210 x 297 mm
    const pageWidth = 210;
    const pageHeight = 297;
    
    // Create A4 PDF
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });
    
    // Border margins: 0.5 cm (5 mm) on all sides for better page utilization
    const margin = 5;
    
    // Sticker dimensions: 38 x 21 mm (width x height as shown in template)
    const stickerWidth = 38;
    const stickerHeight = 21;
    
    // Calculate available space for stickers
    const availableWidth = pageWidth - (2 * margin);
    const availableHeight = pageHeight - (2 * margin);
    
    // Calculate grid layout for 65 stickers
    // 65 stickers = 5 columns x 13 rows
    const columns = 5;
    const rows = 13;
    
    // No spacing between stickers - they should be edge to edge
    const horizontalSpacing = 0;
    const verticalSpacing = 0;
    
    let currentPage = 0;
    let stickersOnCurrentPage = 0;
    const maxStickersPerPage = 65;
    
    // Calculate total stickers needed
    const totalStickers = selectedProducts.reduce((total, product) => total + (product.quantity || 1), 0);
    let stickerIndex = 0;
    
    selectedProducts.forEach(product => {
      const totalStickersForProduct = product.quantity || 1;
      
      for (let i = 0; i < totalStickersForProduct; i++) {
        // Check if we need a new page
        if (stickersOnCurrentPage >= maxStickersPerPage) {
          doc.addPage();
          currentPage++;
          stickersOnCurrentPage = 0;
        }
        
        // Calculate position on current page
        const row = Math.floor(stickersOnCurrentPage / columns);
        const col = stickersOnCurrentPage % columns;
        
        const x = margin + col * (stickerWidth + horizontalSpacing);
        const y = margin + row * (stickerHeight + verticalSpacing);
        
        // Create barcode - compact for 38x21mm sticker
        const canvas = document.createElement('canvas');
        JsBarcode(canvas, product.barcode || product.id.toString(), {
          format: 'CODE128',
          width: 0.8,
          height: 8,
          displayValue: false,
          margin: 0,
          background: '#ffffff'
        });
        const imgData = canvas.toDataURL('image/png');
        
        // Add barcode to sticker (positioned at top)
        doc.addImage(imgData, 'PNG', x + 1, y + 1, stickerWidth - 2, 8);
        
        // Add product info below barcode
        doc.setFontSize(3);
        doc.setFont('helvetica', 'normal');
        
        // Product name (first line) - center aligned
        const productName = product.name || 'Produit';
        const truncatedName = productName.length > 15 ? productName.substring(0, 15) : productName;
        doc.text(truncatedName, x + stickerWidth / 2, y + 12, { align: 'center' });
        
        // Price (second line) - center aligned, directly under name
        const price = product.sale_price || 0;
        const priceText = `${price} MAD`;
        doc.text(priceText, x + stickerWidth / 2, y + 16, { align: 'center' });
        
        // Reference (third line) if available - center aligned
        if (product.reference) {
          const refText = product.reference.length > 12 ? product.reference.substring(0, 12) : product.reference;
          doc.text(`Ref: ${refText}`, x + stickerWidth / 2, y + 19, { align: 'center' });
        }
        
        stickersOnCurrentPage++;
        stickerIndex++;
      }
    });
    
    doc.save('etiquettes_stickers.pdf');
    toast.success('PDF généré avec succès');
  };

  const totalStickers = selectedProducts.reduce((total, p) => total + (p.quantity || 1), 0);
  const totalPages = Math.ceil(totalStickers / 65);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <ModernLoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Étiquettes Stickers</h1>
          <p className="text-neutral-600">Générer des étiquettes avec codes-barres pour vos produits</p>
        </div>
        <div className="flex items-center space-x-3">
          <ModernButton
            variant="primary"
            icon={<Printer className="w-4 h-4" />}
            onClick={generateStickersPDF}
            disabled={selectedProducts.length === 0}
          >
            Générer PDF ({totalStickers} étiquettes)
          </ModernButton>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Produits sélectionnés</p>
              <p className="text-2xl font-bold text-neutral-900">{selectedProducts.length}</p>
            </div>
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
              <Package className="w-6 h-6 text-primary-600" />
            </div>
          </div>
        </ModernCard>

        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Total étiquettes</p>
              <p className="text-2xl font-bold text-success-600">{totalStickers}</p>
            </div>
            <div className="w-12 h-12 bg-success-100 rounded-lg flex items-center justify-center">
              <Printer className="w-6 h-6 text-success-600" />
            </div>
          </div>
        </ModernCard>

        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Pages estimées</p>
              <p className="text-2xl font-bold text-warning-600">{totalPages}</p>
            </div>
            <div className="w-12 h-12 bg-warning-100 rounded-lg flex items-center justify-center">
              <Eye className="w-6 h-6 text-warning-600" />
            </div>
          </div>
        </ModernCard>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Product Search */}
        <ModernCard className="p-6">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-neutral-900 mb-2">Rechercher des produits</h3>
            <div className="relative">
              <ModernSearchBar
                placeholder="Rechercher par nom, marque, modèle ou code-barres..."
                onSearch={setSearchTerm}
                className="w-full"
                debounce={300}
              />
              <button
                type="button"
                onClick={() => setShowAddProductModal(true)}
                className="absolute right-2 top-2 h-8 w-8 flex items-center justify-center bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
                title="Ajouter un nouveau produit"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>
            </div>
          </div>

          {/* Search Results */}
          {searchTerm && (
            <div>
              <h4 className="font-semibold text-neutral-900 mb-3">Résultats de recherche</h4>
              <div className="max-h-64 overflow-y-auto space-y-2">
                {filteredProducts.map(product => (
                  <div
                    key={product.id}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-neutral-50 cursor-pointer"
                    onClick={() => addProductToSelection(product)}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                        <Package className="w-5 h-5 text-primary-600" />
                      </div>
                      <div>
                        <div className="font-medium text-neutral-900">{product.name}</div>
                        <div className="text-sm text-neutral-500">
                          {product.brand && `${product.brand} `}
                          {product.model && `- ${product.model}`}
                        </div>
                        <div className="text-sm text-neutral-600">
                          Prix: {formatCurrency(product.sale_price)}
                          {product.barcode && ` | Code: ${product.barcode}`}
                        </div>
                      </div>
                    </div>
                    <ModernButton
                      variant="ghost"
                      size="sm"
                      icon={<Plus className="w-4 h-4" />}
                    >
                      Ajouter
                    </ModernButton>
                  </div>
                ))}
                {filteredProducts.length === 0 && searchTerm && (
                  <div className="text-center py-8 text-neutral-500">
                    Aucun produit trouvé pour "{searchTerm}"
                  </div>
                )}
              </div>
            </div>
          )}
        </ModernCard>

        {/* Selected Products */}
        <ModernCard className="p-6">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-neutral-900">Produits sélectionnés</h3>
            <p className="text-sm text-neutral-600">
              {selectedProducts.length} produit(s) sélectionné(s) - {totalStickers} étiquettes
            </p>
          </div>

          <div className="max-h-96 overflow-y-auto space-y-3">
            {selectedProducts.map(product => (
              <div
                key={product.id}
                className="p-4 border rounded-lg bg-primary-50"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                      <Package className="w-5 h-5 text-primary-600" />
                    </div>
                    <div>
                      <div className="font-medium text-neutral-900">{product.name}</div>
                      <div className="text-sm text-neutral-500">
                        {product.brand && `${product.brand} `}
                        {product.model && `- ${product.model}`}
                      </div>
                      <div className="text-sm text-neutral-600">
                        Prix: {formatCurrency(product.sale_price)}
                        {product.barcode && ` | Code: ${product.barcode}`}
                      </div>
                    </div>
                  </div>
                  <ModernButton
                    variant="ghost"
                    size="sm"
                    icon={<X className="w-4 h-4" />}
                    onClick={() => removeProductFromSelection(product.id)}
                  >
                    Retirer
                  </ModernButton>
                </div>
                <div className="flex items-center space-x-3">
                  <label className="text-sm font-medium text-neutral-700">Quantité d'étiquettes:</label>
                  <input
                    type="number"
                    min="1"
                    value={product.quantity || 1}
                    onChange={(e) => updateProductQuantity(product.id, Number(e.target.value))}
                    className="w-24 border rounded px-3 py-2 text-sm"
                  />
                  <span className="text-sm text-neutral-600">
                    ({product.quantity || 1} étiquette(s))
                  </span>
                </div>
              </div>
            ))}
            {selectedProducts.length === 0 && (
              <div className="text-center py-8 text-neutral-500">
                Aucun produit sélectionné
              </div>
            )}
          </div>
        </ModernCard>
      </div>

      {/* Preview Modal */}
      <ModernModal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        title="Aperçu des étiquettes"
        size="xl"
      >
        <div className="space-y-4">
          <div className="text-center">
            <p className="text-sm text-neutral-600">
              {totalStickers} étiquettes réparties sur {totalPages} page(s)
            </p>
          </div>
          <div className="grid grid-cols-4 gap-2 p-4 border rounded-lg bg-neutral-50">
            {selectedProducts.slice(0, 8).map((product, index) => (
              <div key={index} className="text-center p-2 border rounded bg-white">
                <div className="text-xs font-medium">{product.name.substring(0, 15)}</div>
                <div className="text-xs text-neutral-500">{product.barcode || product.id}</div>
                <div className="text-xs text-neutral-600">{formatCurrency(product.sale_price)}</div>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-end space-x-2">
            <ModernButton variant="secondary" onClick={() => setShowPreview(false)}>
              Fermer
            </ModernButton>
            <ModernButton
              variant="primary"
              icon={<Download className="w-4 h-4" />}
              onClick={generateStickersPDF}
            >
              Télécharger PDF
            </ModernButton>
          </div>
        </div>
      </ModernModal>

      {/* Add Product Modal */}
      <ModernModal
        isOpen={showAddProductModal}
        onClose={() => setShowAddProductModal(false)}
        title="Ajouter un nouveau produit"
        size="lg"
      >
        <AddProductForm
          onSuccess={handleProductCreated}
          onCancel={() => setShowAddProductModal(false)}
        />
      </ModernModal>
    </div>
  );
};

export default Stickers; 